import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from "../../apiConfig";
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import GreatVibes from '../../fonts/GreatVibes';

const AddressUpdate = () => {
  const [formData, setFormData] = useState({
    registerMobNo: '',
    aadhaarNumber: '',
    address: '',
    pinCode: '',
    state: '',
    district: '',
    postOffice: '',
    image: null
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // ---------------- LOAD DATA FOR EDIT ----------------
  useEffect(() => {
    const id = localStorage.getItem("addressUpdateId");
    if (id) {
      setEditId(id);
      const fetchRecord = async () => {
        try {
          const authToken = localStorage.getItem("authToken");
          const response = await fetch(`${API_BASE_URL}/AddressUpdate/GetAddressUpdateById/${id}`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          if (!response.ok) throw new Error("Failed to fetch record");
          const data = await response.json();
          setFormData({
            registerMobNo: data.registerMobNo || "",
            aadhaarNumber: data.aadhaarNumber || "",
            address: data.address || "",
            pinCode: data.pinCode || "",
            state: data.state || "",
            district: data.district || "",
            postOffice: data.postOffice || "",
            image: null
          });
        } catch (error) {
          console.error("Error loading record:", error);
          Swal.fire("Error", "Could not load record for editing", "error");
        }
      };
      fetchRecord();
    }
  }, []);

  // ---------------- INPUT HANDLER ----------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'aadhaarNumber') {
      const numericValue = value.replace(/\D/g, '').slice(0, 12);
      setFormData(prev => ({
        ...prev,
        aadhaarNumber: numericValue
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ---------------- IMAGE HANDLER ----------------
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      image: file
    }));

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.registerMobNo.trim()) {
      Swal.fire('Error', 'Please enter Register Mobile Number', 'error');
      return;
    }
    if (!formData.aadhaarNumber.trim()) {
      Swal.fire('Error', 'Please enter Aadhaar Number', 'error');
      return;
    }
    if (formData.aadhaarNumber.length !== 12) {
      Swal.fire('Error', 'Aadhaar Number must be 12 digits', 'error');
      return;
    }
    if (!formData.address.trim()) {
      Swal.fire('Error', 'Please enter Address', 'error');
      return;
    }
    if (!formData.pinCode.trim()) {
      Swal.fire('Error', 'Please enter Pin Code', 'error');
      return;
    }
    if (!formData.state.trim()) {
      Swal.fire('Error', 'Please select State', 'error');
      return;
    }
    if (!formData.district.trim()) {
      Swal.fire('Error', 'Please enter District', 'error');
      return;
    }
    if (!formData.postOffice.trim()) {
      Swal.fire('Error', 'Please enter Post Office', 'error');
      return;
    }
    if (!editId && !formData.image) {
      Swal.fire('Error', 'Please upload an image', 'error');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        Swal.fire('Session Expired', 'Please login again', 'warning');
        return;
      }

      setLoading(true);

      const fd = new FormData();
      fd.append('registerMobNo', formData.registerMobNo);
      fd.append('aadhaarNumber', formData.aadhaarNumber);
      fd.append('address', formData.address);
      fd.append('pinCode', formData.pinCode);
      fd.append('state', formData.state);
      fd.append('district', formData.district);
      fd.append('postOffice', formData.postOffice);
      if (formData.image) {
        fd.append('image', formData.image);
      }

      const url = editId
        ? `${API_BASE_URL}/AddressUpdate/Update/${editId}`
        : `${API_BASE_URL}/AddressUpdate/AddressUpdate`;

      const response = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: fd
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit address update');
      }

      Swal.fire('Success', editId ? 'Address record updated successfully' : 'Address update submitted successfully', 'success');

      if (editId) {
        localStorage.removeItem("addressUpdateId");
        setEditId(null);
      }

      setFormData({
        registerMobNo: '',
        aadhaarNumber: '',
        address: '',
        pinCode: '',
        state: '',
        district: '',
        postOffice: '',
        image: null
      });
      setImagePreview(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir',
    'Ladakh', 'Puducherry'
  ];

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card card-default">
            <div className="card-header d-flex justify-content-between">
              <h2 className="card-title">{editId ? 'EDIT ADDRESS UPDATE' : 'ADDRESS UPDATE'}</h2>
              {editId && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    localStorage.removeItem("addressUpdateId");
                    setEditId(null);
                    setFormData({
                      registerMobNo: '',
                      aadhaarNumber: '',
                      address: '',
                      pinCode: '',
                      state: '',
                      district: '',
                      postOffice: '',
                      image: null
                    });
                    setImagePreview(null);
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12">

                    <div className="form-group mb-3">
                      <label>Register Mob No. <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="registerMobNo"
                        value={formData.registerMobNo}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>Aadhaar Number <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="aadhaarNumber"
                        maxLength="12"
                        value={formData.aadhaarNumber}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>Address <span style={{ color: 'red' }}>*</span></label>
                      <textarea
                        className="form-control"
                        name="address"
                        placeholder="Enter Full Address"
                        rows="4"
                        value={formData.address}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>

                    <div className="form-group mb-3">
                      <label>Pin Code <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="pinCode"
                        maxLength="6"
                        value={formData.pinCode}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>State <span style={{ color: 'red' }}>*</span></label>
                      <select
                        className="form-control"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                      >
                        <option value="">SELECT STATE</option>
                        {states.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label>District <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="district"
                        value={formData.district}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>Post Office <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="postOffice"
                        value={formData.postOffice}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>Upload Image {!editId && <span style={{ color: 'red' }}>*</span>}</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageChange}
                        ref={fileInputRef}
                      />
                    </div>

                    {imagePreview && (
                      <div className="form-group mb-3">
                        <label>Image Preview</label>
                        <div className="border rounded p-2">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                <div className="form-footer pt-3">
                  {!editId && (
                    <div className="text-center mb-3">
                      <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Rs . 300</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      type="submit"
                      className="btn btn-primary btn-pill"
                      style={{ minWidth: '150px' }}
                      disabled={loading}
                    >
                      {loading ? 'PROCESSING...' : (editId ? 'UPDATE' : 'SUBMIT')}
                    </button>
                  </div>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressUpdate;


// import React, { useState } from 'react';
// import Swal from 'sweetalert2';
// import jsPDF from 'jspdf';
// import GreatVibes from '../../fonts/GreatVibes';

// const AddressUpdate = () => {
//   const [formData, setFormData] = useState({
//     registerMobNo: '',
//     aadhaarNumber: '',
//     pinCode: '',
//     state: '',
//     district: '',
//     postOffice: '',
//     image: null
//   });

//   const [imagePreview, setImagePreview] = useState(null);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const handleImageChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       setFormData(prev => ({
//         ...prev,
//         image: file
//       }));
      
//       // Create image preview
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setImagePreview(reader.result);
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   const generatePDF = async () => {
//     // Validation
//     if (!formData.registerMobNo.trim()) {
//       Swal.fire('Error', 'Please enter Register Mobile Number', 'error');
//       return;
//     }

//     if (!formData.aadhaarNumber.trim()) {
//       Swal.fire('Error', 'Please enter Aadhaar Number', 'error');
//       return;
//     }

//     if (!formData.pinCode.trim()) {
//       Swal.fire('Error', 'Please enter Pin Code', 'error');
//       return;
//     }

//     if (!formData.state.trim()) {
//       Swal.fire('Error', 'Please select State', 'error');
//       return;
//     }

//     if (!formData.district.trim()) {
//       Swal.fire('Error', 'Please enter District', 'error');
//       return;
//     }

//     if (!formData.postOffice.trim()) {
//       Swal.fire('Error', 'Please enter Post Office', 'error');
//       return;
//     }

//     if (!formData.image) {
//       Swal.fire('Error', 'Please upload an image', 'error');
//       return;
//     }

//     try {
//       const doc = new jsPDF();
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Add font
//       doc.addFileToVFS('GreatVibes-normal.ttf', GreatVibes);
//       doc.addFont('GreatVibes-normal.ttf', 'GreatVibes', 'normal');

//       // Background color
//       doc.setFillColor(240, 240, 240);
//       doc.rect(0, 0, pageWidth, pageHeight, 'F');

//       // Title with handwriting font
//       doc.setFont('GreatVibes', 'normal');
//       doc.setFontSize(28);
//       doc.setTextColor(60, 60, 60);
//       doc.text('ADDRESS UPDATE', pageWidth / 2, 20, { align: 'center' });

//       // Reset to regular font
//       doc.setFont('helvetica', 'normal');
//       doc.setFontSize(11);
//       doc.setTextColor(0, 0, 0);

//       let yPos = 40;

//       // Form fields with data
//       const fields = [
//         { label: 'Register Mob No.', value: formData.registerMobNo },
//         { label: 'Aadhaar Number', value: formData.aadhaarNumber },
//         { label: 'Pin Code', value: formData.pinCode },
//         { label: 'State', value: formData.state },
//         { label: 'District', value: formData.district },
//         { label: 'Post Office', value: formData.postOffice }
//       ];

//       fields.forEach((field) => {
//         // Label
//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(10);
//         doc.text(field.label, 20, yPos);

//         // Value box
//         doc.setDrawColor(100, 100, 100);
//         doc.rect(80, yPos - 5, 100, 7);
        
//         // Value text with handwriting font
//         doc.setFont('GreatVibes', 'normal');
//         doc.setFontSize(14);
//         doc.setTextColor(60, 60, 60);
//         doc.text(field.value.toUpperCase(), 85, yPos + 1);

//         yPos += 18;
//       });

//       // Add image if available
//       if (imagePreview) {
//         yPos += 10;
//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(10);
//         doc.setTextColor(0, 0, 0);
//         doc.text('Attached Document', 20, yPos);
//         yPos += 15;

//         try {
//           doc.addImage(imagePreview, 'JPEG', 20, yPos, 100, 70);
//           yPos += 75;
//         } catch (imgError) {
//           console.error('Error adding image:', imgError);
//         }
//       }

//       // Footer
//       yPos += 10;
//       doc.setFont('GreatVibes', 'normal');
//       doc.setFontSize(16);
//       doc.setTextColor(100, 100, 100);
//       doc.text('Form Submission Charges: Rs. 300', pageWidth / 2, pageHeight - 15, { align: 'center' });

//       // Save PDF
//       doc.save('Address_Update.pdf');
//       Swal.fire('Success', 'PDF downloaded successfully', 'success');
//     } catch (error) {
//       console.error('Error generating PDF:', error);
//       Swal.fire('Error', 'Failed to generate PDF', 'error');
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     // Validation
//     if (!formData.registerMobNo.trim()) {
//       Swal.fire('Error', 'Please enter Register Mobile Number', 'error');
//       return;
//     }

//     if (!formData.aadhaarNumber.trim()) {
//       Swal.fire('Error', 'Please enter Aadhaar Number', 'error');
//       return;
//     }

//     if (!formData.pinCode.trim()) {
//       Swal.fire('Error', 'Please enter Pin Code', 'error');
//       return;
//     }

//     if (!formData.state.trim()) {
//       Swal.fire('Error', 'Please select State', 'error');
//       return;
//     }

//     if (!formData.district.trim()) {
//       Swal.fire('Error', 'Please enter District', 'error');
//       return;
//     }

//     if (!formData.postOffice.trim()) {
//       Swal.fire('Error', 'Please enter Post Office', 'error');
//       return;
//     }

//     if (!formData.image) {
//       Swal.fire('Error', 'Please upload an image', 'error');
//       return;
//     }

//     try {
//       const authToken = localStorage.getItem('authToken');
//       const formDataToSubmit = new FormData();
//       formDataToSubmit.append('registerMobNo', formData.registerMobNo);
//       formDataToSubmit.append('aadhaarNumber', formData.aadhaarNumber);
//       formDataToSubmit.append('pinCode', formData.pinCode);
//       formDataToSubmit.append('state', formData.state);
//       formDataToSubmit.append('district', formData.district);
//       formDataToSubmit.append('postOffice', formData.postOffice);
//       formDataToSubmit.append('image', formData.image);

//       const response = await fetch('https://apiv2.chakran.in/api/AddressUpdate/submit', {
//         method: 'POST',
//         headers: {
//           Authorization: `Bearer ${authToken}`,
//         },
//         body: formDataToSubmit
//       });

//       if (!response.ok) {
//         throw new Error('Failed to submit address update');
//       }

//       const result = await response.json();
//       Swal.fire('Success', 'Address updated successfully', 'success');
      
//       // Reset form
//       setFormData({
//         registerMobNo: '',
//         aadhaarNumber: '',
//         pinCode: '',
//         state: '',
//         district: '',
//         postOffice: '',
//         image: null
//       });
//       setImagePreview(null);
//     } catch (error) {
//       console.error('Error:', error);
//       Swal.fire('Error', error.message || 'Failed to update address', 'error');
//     }
//   };

//   const states = [
//     'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
//     'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
//     'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
//     'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
//     'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
//     'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir',
//     'Ladakh', 'Puducherry'
//   ];

//   return (
//     <div className="container-fluid">
//       <div className="row">
//         <div className="col-12">
//           <div className="card card-default">
//             <div className="card-header">
//               <h2 className="card-title">ADDRESS UPDATE</h2>
//             </div>

//             <div className="card-body">
//               <form onSubmit={handleSubmit}>
//                 <div className="row">
//                   <div className="col-md-12">
//                     <div className="form-group mb-3">
//                       <label htmlFor="registerMobNo">Register Mob No. <span style={{ color: 'red' }}>*</span></label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="registerMobNo"
//                         name="registerMobNo"
//                         placeholder="Enter Mobile Number"
//                         value={formData.registerMobNo}
//                         onChange={handleInputChange}
//                       />
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="aadhaarNumber">Aadhaar Number <span style={{ color: 'red' }}>*</span></label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="aadhaarNumber"
//                         name="aadhaarNumber"
//                         placeholder="Enter Aadhaar Number"
//                         value={formData.aadhaarNumber}
//                         onChange={handleInputChange}
//                         maxLength="12"
//                       />
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="aadhaarNumberConfirm">Aadhaar Number <span style={{ color: 'red' }}>*</span></label>
//                       <textarea
//                         className="form-control"
//                         id="aadhaarNumberConfirm"
//                         placeholder="Confirm Aadhaar Number"
//                         rows="4"
//                         disabled
//                         style={{ backgroundColor: '#f5f5f5' }}
//                       ></textarea>
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="pinCode">Pin Code <span style={{ color: 'red' }}>*</span></label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="pinCode"
//                         name="pinCode"
//                         placeholder="Enter Pin Code"
//                         value={formData.pinCode}
//                         onChange={handleInputChange}
//                         maxLength="6"
//                       />
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="state">State <span style={{ color: 'red' }}>*</span></label>
//                       <select
//                         className="form-control"
//                         id="state"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleInputChange}
//                       >
//                         <option value="">SELECT STATE</option>
//                         {states.map((state) => (
//                           <option key={state} value={state}>
//                             {state}
//                           </option>
//                         ))}
//                       </select>
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="district">District <span style={{ color: 'red' }}>*</span></label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="district"
//                         name="district"
//                         placeholder="Enter District"
//                         value={formData.district}
//                         onChange={handleInputChange}
//                       />
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="postOffice">Post Office <span style={{ color: 'red' }}>*</span></label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="postOffice"
//                         name="postOffice"
//                         placeholder="Enter Post Office"
//                         value={formData.postOffice}
//                         onChange={handleInputChange}
//                       />
//                     </div>

//                     <div className="form-group mb-3">
//                       <label htmlFor="image">Upload Image <span style={{ color: 'red' }}>*</span></label>
//                       <input
//                         type="file"
//                         className="form-control"
//                         id="image"
//                         name="image"
//                         accept="image/*"
//                         onChange={handleImageChange}
//                       />
//                       <small className="form-text text-muted">Supported formats: JPG, PNG, GIF (Max 5MB)</small>
//                     </div>

//                     {imagePreview && (
//                       <div className="form-group mb-3">
//                         <label>Image Preview</label>
//                         <div className="border rounded p-2">
//                           <img
//                             src={imagePreview}
//                             alt="Preview"
//                             style={{
//                               maxWidth: '100%',
//                               maxHeight: '300px',
//                               objectFit: 'contain'
//                             }}
//                           />
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 <div className="form-footer pt-3">
//                   <div className="text-center mb-3">
//                     <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
//                       Rs . 300
//                     </span>
//                   </div>
//                   <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    
//                     <button 
//                       type="submit" 
//                       className="btn btn-primary btn-pill" 
//                       style={{ minWidth: '150px' }}
//                     >
//                       SUBMIT
//                     </button>
//                   </div>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddressUpdate;
